import React, { useEffect, useMemo, useState, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import NarrowSecureLayout from "../../../layout/NarrowSecureLayout.jsx";
import Modal from "../../../components/Modal.jsx";
import { ProjectApi } from "../../../api/authority/generated/index";
import useAuthorityConfiguration from "../../../api/utilities/useAuthorityConfiguration.js";
import { useErrorTranslation } from "../../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import ProjectDomain from "./ProjectDomain.jsx";
import DomainsConfirmationModal from "./DomainsConfirmationModal.jsx";
import Button from "../../../components/Button.jsx";
import LoadingComponent from "../../../components/LoadingComponent.jsx";
import {useNavigate} from "react-router-dom";
import {formatMaximumDomains, getMaximumDomains, groupDomainsByActivationAndHierarchy, isDomainLimitAvailable, isSingleDomainPackage} from "./domainPresentation.js";

export default function ProjectDomains({ project }) {
    const { t } = useTranslation();
    const { et } = useErrorTranslation();
    const authorityConfiguration = useAuthorityConfiguration();
    const navigate = useNavigate();

    // State hooks for managing domains, changes, modals, loading, and price/payment info
    const [domainsWithStats, setDomainsWithStats] = useState([]);
    const [domainsLoading, setDomainsLoading] = useState(true);
    const [domainLimit, setDomainLimit] = useState(null);
    const [domainLimitLoading, setDomainLimitLoading] = useState(true);
    const [changedDomains, setChangedDomains] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [addDomainModalOpened, setAddDomainModalOpened] = useState(false);
    const [addDomainText, setAddDomainText] = useState('');
    const [addDomainLoading, setAddDomainLoading] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [domainPriceQuote, setDomainPriceQuote] = useState(null);
    const [domainsToPay, setDomainsToPay] = useState(0);

    const handleUpgradePlan = () => {
        navigate(`/project/${project.id}/settings/subscription`);
    };

    // Refs to prevent unwanted repeated operations (for React Strict Mode)
    const isInitialMount = useRef(true);
    const fetchInitiatedRef = useRef(new Map());
    const fetchGenerationRef = useRef(0);
    const domainLimitGenerationRef = useRef(0);
    const domainUpdateInFlightRef = useRef(false);

    // Domain limit metadata is resolver-backed; do not infer entitlements from a stale subscription snapshot.
    const hasDomainLimit = isDomainLimitAvailable(domainLimit);
    const isFreePackage = hasDomainLimit && isSingleDomainPackage(domainLimit);

    // Calculate the count of currently enabled domains (including unsaved state)
    const selectedEnabled = useMemo(() => {
        return Object.values(domainsWithStats).filter((d) =>
            changedDomains[d.projectDomain.hostName] !== undefined
                ? changedDomains[d.projectDomain.hostName]
                : d.projectDomain.isEnabled
        ).length;
    }, [domainsWithStats, changedDomains]);

    // Maximum domains allowed by the authoritative package resolver.
    const maximumDomains = useMemo(() => {
        return getMaximumDomains(domainLimit);
    }, [domainLimit]);

    const fetchDomainLimit = async () => {
        const generation = ++domainLimitGenerationRef.current;
        setDomainLimit(null);
        setDomainLimitLoading(true);

        try {
            const response = await new ProjectApi(authorityConfiguration).getProjectDomainsLimit({
                projectId: project.id,
            });
            if (generation !== domainLimitGenerationRef.current) return;
            setDomainLimit(response);
        } catch (e) {
            if (generation !== domainLimitGenerationRef.current) return;
            const message = await et(e);
            if (generation === domainLimitGenerationRef.current && message) toast.error(message);
        } finally {
            if (generation === domainLimitGenerationRef.current) setDomainLimitLoading(false);
        }
    };

    // Fetches project domains from API and handles possible errors
    const fetchDomains = async () => {
        const generation = ++fetchGenerationRef.current;
        setDomainsWithStats([]);
        setChangedDomains({});
        setDomainsLoading(true);
        try {
            const response = await new ProjectApi(authorityConfiguration).listProjectDomains({
                projectId: project.id,
            });
            if (generation !== fetchGenerationRef.current) return;
            setDomainsWithStats(response || []);
            setDomainsLoading(false);
        } catch (e) {
            if (generation !== fetchGenerationRef.current) return;
            et(e).then((message) => { if (message) toast.error(message); });
        }
    };

    // Loads domains when project is ready or changes
    useEffect(() => {
        if (!project?.id) return;

        const fetchData = async () => {
            // Avoid double-fetching, especially in React StrictMode
            if (isInitialMount.current && fetchInitiatedRef.current.has(project.id)) {
                return;
            }
            if (isInitialMount.current) {
                fetchInitiatedRef.current.set(project.id, true);
            }
            await Promise.all([fetchDomains(), fetchDomainLimit()]);
        };

        fetchData();

        if (isInitialMount.current) {
            isInitialMount.current = false;
        }

        // Cleanup (removes completed flag when component unmounts)
        return () => {
            fetchGenerationRef.current += 1;
            domainLimitGenerationRef.current += 1;
            fetchInitiatedRef.current.delete(project.id);
        };
    }, [project?.id]);

    // Handles the addition of new domain when loading flag is set
    useEffect(() => {
        if (!addDomainLoading) return;

        new ProjectApi(authorityConfiguration)
            .createOrUpdateProjectDomain({
                projectId: project.id,
                createOrUpdateProjectDomainInput: {
                    isEnabled: false,
                    hostName: addDomainText,
                },
            })
            .then(() => {
                setAddDomainText('');
                setAddDomainModalOpened(false);
                fetchDomains();
            })
            .catch((e) => et(e).then((message) => { if (message) toast.error(message); }))
            .finally(() => setAddDomainLoading(false));
    }, [addDomainLoading, project.id, authorityConfiguration, et, addDomainText]);

    // Handle logic for toggles, allow only one enabled when free or missing subscription
    const handleToggleChange = (hostName, newValue) => {
        if (isFreePackage && newValue) {
            // If any other domain is already enabled, do not allow enabling this one
            const anotherEnabled = Object.values(domainsWithStats)
                .some((d) =>
                    (changedDomains[d.projectDomain.hostName] !== undefined
                        ? changedDomains[d.projectDomain.hostName]
                        : d.projectDomain.isEnabled
                    ) &&
                    d.projectDomain.hostName !== hostName
                );
            if (anotherEnabled) {
                return;
            }
        }
        setChangedDomains((prev) => {
            const newChangedDomains = { ...prev };
            const originalDomain = Object.values(domainsWithStats).find(
                (d) => d.projectDomain.hostName === hostName
            );
            if (originalDomain && originalDomain.projectDomain.isEnabled === newValue) {
                delete newChangedDomains[hostName];
            } else {
                newChangedDomains[hostName] = newValue;
            }
            return newChangedDomains;
        });
    };

    // Save changes: check prices before commit, open confirmation if needed
    const handleSaveChanges = () => {
        setIsSaving(true);

        const domainUpdates = Object.entries(changedDomains).map(([hostName, isEnabled]) => ({
            hostName,
            isEnabled,
        }));

        // Gets possible additional price for requested change
        new ProjectApi(authorityConfiguration)
            .getDomainsStatusChangePrice({
                projectId: project.id,
                createOrUpdateProjectDomainInput: domainUpdates,
            })
            .then((response) => {
                setDomainsToPay(response.domainsToPay);
                const price = response.totalAmount;
                if (price > 0) {
                    setDomainPriceQuote(response);
                    setIsConfirmationModalOpen(true);
                    setIsSaving(false);
                } else {
                    performDomainUpdate(domainUpdates);
                }
            })
            .catch((e) => {
                et(e).then((message) => { if (message) toast.error(message); });
                setIsSaving(false);
            });
    };

    // Perform the actual update/commit of domain changes
    const performDomainUpdate = async (domainUpdates) => {
        if (domainUpdateInFlightRef.current) return false;

        domainUpdateInFlightRef.current = true;
        setIsSaving(true);
        try {
            await new ProjectApi(authorityConfiguration).updateProjectDomainsStatus({
                projectId: project.id,
                createOrUpdateProjectDomainInput: domainUpdates,
            });
            setChangedDomains({});
            toast.success(t('project.domains-updated'));
            await fetchDomains();
            return true;
        } catch (e) {
            const message = await et(e);
            if (message) toast.error(message);
            return false;
        } finally {
            domainUpdateInFlightRef.current = false;
            setIsSaving(false);
        }
    };

    // Confirms price-based domain change in modal
    const handleConfirmDomainChange = async () => {
        const domainUpdates = Object.entries(changedDomains).map(([hostName, isEnabled]) => ({
            hostName,
            isEnabled,
        }));
        const updated = await performDomainUpdate(domainUpdates);
        if (updated) setIsConfirmationModalOpen(false);
    };

    // Cancels any changes (reset state)
    const handleCancelChanges = () => {
        setChangedDomains({});
    };

    // Check if there are any unsaved changes
    const hasChanges = Object.keys(changedDomains).length > 0;

    // State for disabling save button on FREE
    const isSaveDisabled = useMemo(() => {
        if (!hasChanges || isSaving || !hasDomainLimit) return true;
        if (isFreePackage) {
            // Allow button only if exactly one domain is enabled
            // Taking into account changes and current state
            return selectedEnabled !== 1;
        }
        return false;
    }, [hasChanges, isSaving, isFreePackage, selectedEnabled, hasDomainLimit]);

    const [primaryDomains, secondaryDomains] = useMemo(() => {
        const primary = [];
        const secondary = [];
        groupDomainsByActivationAndHierarchy(domainsWithStats).forEach((family) => {
            const isPrimaryFamily = family.some((domainWithStats) => (
                domainWithStats.projectDomain.isBuiltin === false
                || domainWithStats.projectDomainStats.orderCount > 0
            ));
            (isPrimaryFamily ? primary : secondary).push(...family);
        });

        return [primary, secondary];
    }, [domainsWithStats]);

    const renderDomain = (domainWithStats) => {
        const isThisDisabled =
            isFreePackage && (
                !(
                    changedDomains[domainWithStats.projectDomain.hostName] !== undefined
                        ? changedDomains[domainWithStats.projectDomain.hostName]
                        : domainWithStats.projectDomain.isEnabled
                ) &&
                selectedEnabled >= 1
            );
        const isEnabled = changedDomains[domainWithStats.projectDomain.hostName] !== undefined
            ? changedDomains[domainWithStats.projectDomain.hostName]
            : domainWithStats.projectDomain.isEnabled;

        return (
            <ProjectDomain
                key={domainWithStats.projectDomain.hostName}
                project={project}
                domainWithStats={domainWithStats}
                onReload={fetchDomains}
                onToggleChange={handleToggleChange}
                isSaving={isSaving}
                isDisabled={!hasDomainLimit || isThisDisabled}
                isEnabled={isEnabled}
            />
        );
    };

    return [
        <NarrowSecureLayout
            key={'pd-page-content'}
            hiddenAlertIds={isFreePackage ? ['missing-subscription'] : []}
            compactHeaderSpacing
            title={
                <>
                    {t('project.domains')}
                    {!domainsLoading && domainsWithStats && (
                        <span className="text-sm text-violet-500 self-end mb-1">
                            {selectedEnabled}/{formatMaximumDomains(hasDomainLimit ? maximumDomains : null)}
                        </span>
                    )}
                </>
            }
            actions={
                <Button
                    variant="black"
                    onClick={() => setAddDomainModalOpened(true)}
                >
                    {t('project.add-domain')}
                </Button>
            }
        >
            {isFreePackage && (
                <div
                    className="app-surface mb-4 w-full h-full flex w-full text-normal text-amber-950 dark:text-amber-100 justify-between items-center text-left py-3 px-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 shadow-sm border border-amber-200 dark:border-amber-800 transition"
                >
                    {t('project.no-active-package-text')}
                    <Button type="button" variant="outline-warning" onClick={handleUpgradePlan}>
                        {t('domains.change-tarif')}
                    </Button>
                </div>
            )}

            <div className={'mb-6 rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-100'}>
                <div className={'font-semibold'}>{t('project.domains-description-title', {defaultValue: 'Jak to funguje'})}</div>
                <div className={'mt-1 text-violet-800 dark:text-violet-200'}>{t('project.domains-description', {defaultValue: 'Přidejte domény, které chcete sledovat, a aktivujte je podle limitu svého balíčku. Hlavní domény jsou ručně přidané nebo mají objednávky, vedlejší se automaticky doplňují z cílových adres reklam.'})}</div>
            </div>

            <LoadingComponent loading={domainsLoading}>
            {primaryDomains.length > 0 && (
                <section className={'mb-6'}>
                    <h2 className={'mb-3 text-lg font-semibold'}>{t('project.primary-domains')}</h2>
                    {primaryDomains.map(renderDomain)}
                </section>
            )}

            {secondaryDomains.length > 0 && (
                <section>
                    <h2 className={'mb-3 text-lg font-semibold'}>{t('project.secondary-domains')}</h2>
                    {secondaryDomains.map(renderDomain)}
                </section>
            )}

            {hasChanges && (
                <div className={'flex justify-end mt-6 gap-2'}>
                    <Button
                        variant="outline-black"
                        onClick={handleCancelChanges}
                        disabled={isSaving}
                    >
                        {t('domains.discard-changes')}
                    </Button>
                    <Button
                        variant="black"
                        onClick={handleSaveChanges}
                        loading={isSaving}
                        disabled={isSaveDisabled}
                    >
                        {isSaving ? t('saving') : t('save-changes')}
                    </Button>
                </div>
            )}
            </LoadingComponent>

            {/* Add domain modal */}
            <Modal
                key={'pd-add-domain-modal'}
                className={'w-full lg:w-11/12 max-w-lg'}
                visible={addDomainModalOpened}
                title={t('project.add-domain')}
                setVisible={setAddDomainModalOpened}
                loading={addDomainLoading}
            >
                <div className={'space-y-5'}>
                    <label className={'block'}>
                        <span className={'mb-1 block text-sm font-medium'}>{t('project.domain')}</span>
                        <span className={'flex min-w-0 overflow-hidden rounded-lg border border-gray-300 bg-white transition focus-within:border-violet-500 focus-within:ring-2 focus-within:ring-violet-100 dark:border-gray-600 dark:bg-gray-900 dark:focus-within:border-violet-400 dark:focus-within:ring-violet-900'}>
                            <span className={'flex shrink-0 items-center border-r border-gray-300 bg-gray-50 px-3 text-sm text-gray-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100'}>https://</span>
                            <input
                                className={'form-input min-w-0 flex-1 rounded-r-lg border-0 shadow-none placeholder:text-gray-400 focus:ring-0 dark:placeholder:text-gray-500'}
                                type={'text'}
                                value={addDomainText}
                                onChange={(e) => setAddDomainText(e.target.value)}
                                placeholder={t('project.add-domain-placeholder', {defaultValue: 'domena.cz nebo www.domena.cz'})}
                            />
                        </span>
                    </label>
                    <div className={'rounded-lg border border-violet-200 bg-violet-50 p-4 text-sm text-violet-900 dark:border-violet-900/60 dark:bg-violet-950/30 dark:text-violet-100'}>
                        <div className={'font-semibold'}>{t('project.subdomain-tracking-title', {defaultValue: 'Chcete subdoménu sledovat samostatně?'})}</div>
                        <div className={'mt-1 text-violet-800 dark:text-violet-200'}>
                            <Trans
                                i18nKey="project.subdomain-tracking-description"
                                defaults="Přidejte ji zvlášť, například <strong>b2b.domena.cz</strong>. Jinak se její reklamy a objednávky přiřadí k hlavní doméně."
                                components={{strong: <strong className="font-semibold text-gray-900 dark:text-gray-50"/>}}
                            />
                        </div>
                    </div>
                    <div className={'flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end dark:border-gray-700'}>
                        <Button
                            variant="black"
                            className={'w-full sm:w-auto'}
                            onClick={() => setAddDomainLoading(true)}
                        >
                            {t('project.add-domain')}
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* Confirmation modal for possible extra price */}
            <DomainsConfirmationModal
                key="pd-confirmation-modal"
                isOpen={isConfirmationModalOpen}
                onConfirm={handleConfirmDomainChange}
                onClose={() => setIsConfirmationModalOpen(false)}
                priceQuote={domainPriceQuote}
                domainsToPay={domainsToPay}
                project={project}
            />
        </NarrowSecureLayout>
    ];
}
